import React from 'react';
import AlertProvider from '../AlertProvider';
import AssetProvider from '../AssetProvider';
import AssetTransactionProvider from '../AssetTransactionProvider';
import AssetExportConfigurationProvider from '../AssetExportConfigurationProvider';
import AuthenticationSettingsProvider from '../AuthenticationSettingsProvider';
import BinProvider from '../BinProvider';
import BinStatusProvider from '../BinStatusProvider';
import CommentProvider from '../CommentProvider';
import ContactProvider from '../ContactProvider';
import ContainerProvider from '../ContainerProvider';
import ContainerTypeProvider from '../ContainerTypeProvider';
import CsvUploadConfigurationProvider from '../CsvUploadConfigurationProvider';
import CustomFieldProvider from '../CustomFieldProvider';
import FileProvider from '../FileProvider';
import LotProvider from '../LotProvider';
import OrderExportConfigurationProvider from '../OrderExportConfigurationProvider';
import OrderProvider from '../OrderProvider';
import ProductExportConfigurationProvider from '../ProductExportConfigurationProvider';
import ProductMasterDataExportConfigurationProvider from '../ProductMasterDataExportConfigurationProvider';
import ProductMasterDataProvider from '../ProductMasterDataProvider';
import ProductProvider from '../ProductProvider';
import ProductTransactionExportConfigurationProvider from '../ProductTransactionExportConfigurationProvider';
import ProductTransactionProvider from '../ProductTransactionProvider';
import ReorderRuleExportConfigurationProvider from '../ReorderRuleExportConfigurationProvider';
import ReorderRuleProvider from '../ReorderRuleProvider';
import ReorderUserRuleProvider from '../ReorderUserRuleProvider';
import ScanRuleProvider from '../ScanRuleProvider';
import StockLocationProvider from '../StockLocationProvider';
import StockLocationRoleAssignmentProvider from '../StockLocationRoleAssignmentProvider';
import TagProvider from '../TagProvider';
import TagRelationProvider from '../TagRelationProvider';
import TaskExportConfigurationProvider from '../TaskExportConfigurationProvider';
import TaskProvider from '../TaskProvider';
import TaskSettingsProvider from '../TaskSettingsProvider';
import TriggerProvider from '../TriggerProvider';
import IntegrationProvider from '../IntegrationProvider';
import HandlingUnitProvider from '../HandlingUnitProvider';
import KitTemplateProvider from '../KitTemplateProvider';
import KitProvider from '../KitProvider';
import BarcodeExportConfigurationProvider from '../BarcodeExportConfigurationProvider';
import BinConstraintProvider from '../BinConstraintProvider';
import PdfTemplateProvider from '../PdfTemplateProvider';
import TableSettingsProvider from '../TableSettingsProvider';

interface ProviderProps {
  children: React.ReactNode;
  alert?: boolean;
  asset?: boolean;
  assetExportConfiguration?: boolean;
  assetTransaction?: boolean;
  authenticationSettings?: boolean;
  barcodeExportConfiguration?: boolean;
  billing?: boolean;
  bin?: boolean;
  binStatus?: boolean;
  binConstraint?: boolean;
  comment?: boolean;
  contact?: boolean;
  container?: boolean;
  containerType?: boolean;
  csvUploadConfiguration?: boolean;
  customField?: boolean;
  dashboard?: boolean;
  file?: boolean;
  handlingUnit?: boolean;
  integration?: boolean;
  kit?: boolean;
  kitTemplates?: boolean;
  lot?: boolean;
  order?: boolean;
  orderExportConfiguration?: boolean;
  pdfTemplate?: boolean;
  product?: boolean;
  productExportConfiguration?: boolean;
  productMasterData?: boolean;
  productMasterDataExportConfiguration?: boolean;
  productTransaction?: boolean;
  productTransactionExportConfiguration?: boolean;
  reorderRule?: boolean;
  reorderRuleExportConfiguration?: boolean;
  reorderUserRule?: boolean;
  scanRule?: boolean;
  stockLocation?: boolean;
  stockLocationRoleAssignment?: boolean;
  tableSettings?: boolean;
  tag?: boolean;
  tagRelation?: boolean;
  task?: boolean;
  taskExportConfiguration?: boolean;
  taskSettings?: boolean;
  trigger?: boolean;
  user?: boolean;
}

export default function Provider({ children, ...props }: ProviderProps) {
  let result = children;

  if (props.productTransaction) {
    result = <ProductTransactionProvider>{result}</ProductTransactionProvider>;
  }

  if (props.product) {
    result = <ProductProvider>{result}</ProductProvider>;
  }

  if (props.alert) {
    result = <AlertProvider>{result}</AlertProvider>;
  }

  if (props.assetTransaction) {
    result = <AssetTransactionProvider>{result}</AssetTransactionProvider>;
  }

  if (props.asset) {
    result = <AssetProvider>{result}</AssetProvider>;
  }

  if (props.assetExportConfiguration) {
    result = <AssetExportConfigurationProvider>{result}</AssetExportConfigurationProvider>;
  }

  if (props.bin) {
    result = <BinProvider>{result}</BinProvider>;
  }

  if (props.container) {
    result = <ContainerProvider>{result}</ContainerProvider>;
  }

  if (props.lot) {
    result = <LotProvider>{result}</LotProvider>;
  }

  if (props.order) {
    result = <OrderProvider>{result}</OrderProvider>;
  }

  if (props.task) {
    result = <TaskProvider>{result}</TaskProvider>;
  }

  if (props.productMasterData) {
    result = <ProductMasterDataProvider>{result}</ProductMasterDataProvider>;
  }

  if (props.stockLocation) {
    result = <StockLocationProvider>{result}</StockLocationProvider>;
  }

  if (props.reorderRule) {
    result = <ReorderRuleProvider>{result}</ReorderRuleProvider>;
  }

  if (props.reorderUserRule) {
    result = <ReorderUserRuleProvider>{result}</ReorderUserRuleProvider>;
  }

  if (props.contact) {
    result = <ContactProvider>{result}</ContactProvider>;
  }

  if (props.file) {
    result = <FileProvider>{result}</FileProvider>;
  }

  if (props.comment) {
    result = <CommentProvider>{result}</CommentProvider>;
  }

  if (props.handlingUnit) {
    result = <HandlingUnitProvider>{result}</HandlingUnitProvider>;
  }

  if (props.kitTemplates) {
    result = <KitTemplateProvider>{result}</KitTemplateProvider>;
  }

  if (props.kit) {
    result = <KitProvider>{result}</KitProvider>;
  }

  if (props.barcodeExportConfiguration) {
    result = <BarcodeExportConfigurationProvider>{result}</BarcodeExportConfigurationProvider>;
  }

  if (props.binConstraint) {
    result = <BinConstraintProvider>{result}</BinConstraintProvider>;
  }

  if (props.pdfTemplate) {
    result = <PdfTemplateProvider>{result}</PdfTemplateProvider>;
  }

  // ROLE ASSIGNMENTS

  if (props.stockLocationRoleAssignment) {
    result = <StockLocationRoleAssignmentProvider>{result}</StockLocationRoleAssignmentProvider>;
  }

  // EXPORT CONFIGURATIONS

  if (props.taskExportConfiguration) {
    result = <TaskExportConfigurationProvider>{result}</TaskExportConfigurationProvider>;
  }

  if (props.orderExportConfiguration) {
    result = <OrderExportConfigurationProvider>{result}</OrderExportConfigurationProvider>;
  }

  if (props.productExportConfiguration) {
    result = <ProductExportConfigurationProvider>{result}</ProductExportConfigurationProvider>;
  }

  if (props.productMasterDataExportConfiguration) {
    result = <ProductMasterDataExportConfigurationProvider>{result}</ProductMasterDataExportConfigurationProvider>;
  }

  if (props.productTransactionExportConfiguration) {
    result = <ProductTransactionExportConfigurationProvider>{result}</ProductTransactionExportConfigurationProvider>;
  }

  if (props.reorderRuleExportConfiguration) {
    result = <ReorderRuleExportConfigurationProvider>{result}</ReorderRuleExportConfigurationProvider>;
  }

  // SETTINGS

  if (props.tag) {
    result = <TagProvider>{result}</TagProvider>;
  }

  if (props.tagRelation) {
    result = <TagRelationProvider>{result}</TagRelationProvider>;
  }

  if (props.binStatus) {
    result = <BinStatusProvider>{result}</BinStatusProvider>;
  }

  if (props.containerType) {
    result = <ContainerTypeProvider>{result}</ContainerTypeProvider>;
  }

  if (props.csvUploadConfiguration) {
    result = <CsvUploadConfigurationProvider>{result}</CsvUploadConfigurationProvider>;
  }

  if (props.customField) {
    result = <CustomFieldProvider>{result}</CustomFieldProvider>;
  }

  if (props.trigger) {
    result = <TriggerProvider>{result}</TriggerProvider>;
  }

  if (props.scanRule) {
    result = <ScanRuleProvider>{result}</ScanRuleProvider>;
  }

  if (props.integration) {
    result = <IntegrationProvider>{result}</IntegrationProvider>;
  }

  // TABLE SETTINGS
  if (props.tableSettings) {
    result = <TableSettingsProvider>{result}</TableSettingsProvider>;
  }

  // ENTITY SETTINGS

  if (props.taskSettings) {
    result = <TaskSettingsProvider>{result}</TaskSettingsProvider>;
  }

  if (props.authenticationSettings) {
    result = <AuthenticationSettingsProvider>{result}</AuthenticationSettingsProvider>;
  }

  return result as JSX.Element;
}
